import type { ReactNode } from 'react';
import { useCallback, useEffect, useState } from 'react';

import { ThemeContext } from '@/hooks/useTheme';
import type { BootstrapTheme } from '@/types/theme';
import { bootstrapThemes } from '@/types/theme';

interface ThemeContextProviderProps {
  readonly children: ReactNode;
}

const STORAGE_KEY = 'datex-theme';

function hexToRgb(hex: string): string {
  const h = hex.replace('#', '');
  const normalized =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

export function ThemeContextProvider({
  children,
}: ThemeContextProviderProps): React.JSX.Element {
  const [currentTheme, setCurrentTheme] = useState<BootstrapTheme>(
    bootstrapThemes[0]
  );

  const applyTheme = useCallback((theme: BootstrapTheme): void => {
    const root = document.documentElement;

    // Set Bootstrap theme mode
    root.setAttribute('data-bs-theme', theme.isDark ? 'dark' : 'light');
    root.setAttribute('data-theme', theme.id);

    // Apply all theme variables
    Object.entries(theme.variables).forEach(([prop, val]) => {
      root.style.setProperty(prop, val);
    });

    // Generate RGB values for colors that need them
    const hexKeys = [
      '--bs-primary',
      '--bs-secondary',
      '--bs-success',
      '--bs-info',
      '--bs-warning',
      '--bs-danger',
      '--bs-light',
      '--bs-dark',
      '--bs-body-bg',
      '--bs-body-color',
    ];

    for (const key of hexKeys) {
      const value = getComputedStyle(root).getPropertyValue(key).trim();
      if (value.startsWith('#')) {
        const rgbKey = key.replace(/-bg$|-color$/, '') + '-rgb';
        root.style.setProperty(rgbKey, hexToRgb(value));
      }
    }

    // Standard Bootstrap shadows
    root.style.setProperty(
      '--bs-box-shadow-sm',
      '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)'
    );
    root.style.setProperty(
      '--bs-box-shadow',
      '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
    );
    root.style.setProperty(
      '--bs-box-shadow-lg',
      '0 1rem 3rem rgba(0, 0, 0, 0.175)'
    );
    root.style.setProperty('--bs-border-radius-sm', '0.25rem');
    root.style.setProperty('--bs-border-radius-lg', '0.5rem');
  }, []);

  const setTheme = useCallback(
    (themeId: string): void => {
      const theme = bootstrapThemes.find((t) => t.id === themeId);
      if (theme) {
        setCurrentTheme(theme);
        applyTheme(theme);
        localStorage.setItem(STORAGE_KEY, themeId);
      }
    },
    [applyTheme]
  );

  useEffect(() => {
    const savedThemeId = localStorage.getItem(STORAGE_KEY);
    const initialTheme = savedThemeId
      ? (bootstrapThemes.find((t) => t.id === savedThemeId) ?? bootstrapThemes[0])
      : bootstrapThemes[0];

    setCurrentTheme(initialTheme);
    applyTheme(initialTheme);
  }, [applyTheme]);

  return (
    <ThemeContext.Provider
      value={{
        currentTheme,
        setTheme,
        availableThemes: bootstrapThemes,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
